@if (loading()) {
  <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
    <hlm-spinner size="20" /> Loading user account...
  </div>
}

@if (error(); as err) {
  <cmt-error-state
    title="Failed to load user account"
    [message]="err"
    (retry)="reloadUserAccount()" />
}

@if (userAccount(); as ua) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-6">

    <!-- HERO CARD -->
    <div class="mb-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="flex flex-wrap items-center gap-2 cmt-eyebrow">
            <span>User Account &middot; UA {{ ua.id }}</span>
            <hlm-badge [variant]="statusBadgeTone()">
              <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
              {{ statusLabel() }}
            </hlm-badge>
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            {{ ua.firstName }} {{ ua.lastName }}
            <span class="font-normal text-muted-foreground">({{ ua.username }})</span>
          </h1>
          <p class="text-sm text-muted-foreground">{{ ua.email }}</p>
        </div>
        @if (!editMode()) {
          <div class="flex flex-none flex-col items-end gap-1 pt-1">
            @if (userAccountActive()) {
              <button hlmBtn variant="destructive" size="sm" (click)="deactivate()"
                      data-testid="ua-deactivate-btn">
                <hlm-icon name="lucideBan" size="0.9rem" />
                Deactivate
              </button>
              <!-- EB-3811/EB-3856: short explainer beneath the destructive action so
                   operators know the scope at a glance — UA deactivation stops CAI
                   call handling for this user. text-sm + text-foreground for
                   readability on the warm parchment card (see CLAUDE.md → Inline
                   action explainer). -->
              <p class="max-w-[16rem] text-right text-sm leading-snug text-foreground"
                 data-testid="ua-deactivate-scope-hint">
                Deactivates the call handling.
              </p>
            } @else {
              <button hlmBtn variant="default" size="sm" (click)="reactivate()"
                      data-testid="ua-reactivate-btn">
                <hlm-icon name="lucideCheck" size="0.9rem" />
                Reactivate
              </button>
            }
          </div>
        }
      </div>
    </div>

    <!-- Sub-hero actions: portal launches + cross-entity jumps (right-aligned).
         items-start so the conditional "Disabled — ..." inline hints below the
         disable-able buttons (CLAUDE.md → Inline action explainer) don't offset
         their neighbours vertically. -->
    @if (!editMode()) {
      <div class="-mt-4 mb-1 flex flex-wrap items-start justify-end gap-1">
        <div class="flex flex-col items-start gap-1">
          <button hlmBtn variant="default" (click)="openNewPortal()"
                  [disabled]="newPortalChecked() && !newPortalUrl()"
                  data-testid="ua-open-new-portal-btn">
            <hlm-icon name="lucideExternalLink" size="0.9rem" />
            Open NEW Portal
          </button>
          @if (newPortalChecked() && !newPortalUrl()) {
            <p class="max-w-[12rem] text-sm leading-snug text-foreground"
               data-testid="ua-open-new-portal-disabled-hint">
              Disabled — not available for this customer.
            </p>
          }
        </div>
        <button hlmBtn variant="outline" (click)="openOldPortal()">
          <hlm-icon name="lucideExternalLink" size="0.9rem" />
          Open OLD Portal
        </button>
        <div class="mx-1 h-6 w-px bg-border" aria-hidden="true"></div>
        <div class="flex flex-col items-start gap-1">
          <button hlmBtn variant="outline"
                  [disabled]="!canGoToContract()"
                  (click)="goToContract()"
                  data-testid="ua-go-to-contract-btn">
            <hlm-icon name="lucideFileText" size="0.9rem" />
            Go to Contract
          </button>
          @if (!canGoToContract()) {
            <p class="max-w-[14rem] text-sm leading-snug text-foreground"
               data-testid="ua-go-to-contract-disabled-hint">
              Disabled — no phone service linked to this account.
            </p>
          }
        </div>
        <button hlmBtn variant="outline" (click)="openCallViewerForUa()">
          <hlm-icon name="lucidePhone" size="0.9rem" />
          Open Call Viewer
        </button>
      </div>
    }

    <!-- Two-column: Personal Data + Account Settings -->
    <div class="grid gap-5 lg:grid-cols-2">

      <!-- Personal Data -->
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideUserRound" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Personal Data</h2>
        </header>

        <dl class="flex flex-col divide-y divide-border/70">
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Salutation</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" class="text-left" [formControl]="form.controls.salutation" />
              } @else {
                <span [class]="'text-sm font-medium ' + (ua.salutation ? 'text-foreground' : 'text-muted-foreground/60')">
                  {{ ua.salutation || '—' }}
                </span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Academic Title</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" class="text-left" [formControl]="form.controls.academicTitle" />
              } @else {
                <span [class]="'text-sm font-medium ' + (ua.academicTitle ? 'text-foreground' : 'text-muted-foreground/60')">
                  {{ ua.academicTitle || '—' }}
                </span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">First Name</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" class="text-left" [formControl]="form.controls.firstName" />
              } @else {
                <span class="text-sm font-medium text-foreground">{{ ua.firstName }}</span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Last Name</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" class="text-left" [formControl]="form.controls.lastName" />
              } @else {
                <span class="text-sm font-medium text-foreground">{{ ua.lastName }}</span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Email</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" type="email" class="text-left" [formControl]="form.controls.email" />
              } @else if (ua.email) {
                <span class="text-sm font-medium text-foreground">
                  {{ ua.email }}
                  @if (ua.emailConfirmed) {
                    <hlm-badge variant="success" class="ml-1.5">confirmed</hlm-badge>
                  } @else {
                    <hlm-badge variant="destructive" class="ml-1.5">not confirmed</hlm-badge>
                  }
                </span>
              } @else {
                <span class="text-sm font-medium text-muted-foreground/60">—</span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Mobile</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" class="text-left" [formControl]="form.controls.mobile" />
              } @else {
                <span class="text-sm font-medium text-foreground">
                  {{ ua.mobile || '—' }}
                  @if (ua.mobile) {
                    @if (ua.mobileConfirmed) {
                      <hlm-badge variant="success" class="ml-1.5">confirmed</hlm-badge>
                    } @else {
                      <hlm-badge variant="destructive" class="ml-1.5">not confirmed</hlm-badge>
                    }
                  }
                </span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Birthday</dt>
            <dd [class]="'font-mono-feat text-sm ' + (ua.birthday ? 'text-foreground' : 'text-muted-foreground/60')">
              {{ ua.birthday ? (ua.birthday | date:'dd.MM.yyyy') : '—' }}
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Username / Login</dt>
            <dd class="min-w-0 flex-1 text-right">
              @if (editMode()) {
                <input hlmInput size="sm" class="text-left" [formControl]="form.controls.username" />
              } @else {
                <span class="font-mono-feat text-sm text-foreground">{{ ua.username }}</span>
              }
            </dd>
          </div>
        </dl>
      </section>

      <!-- Account Settings -->
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucideSettings" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Account Settings</h2>
        </header>

        <dl class="flex flex-col divide-y divide-border/70">
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Language</dt>
            <dd class="text-sm font-medium text-foreground">{{ ua.language }}</dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">Email Sender Address</dt>
            <dd [class]="'text-sm font-medium ' + (ua.emailSenderAddress ? 'text-foreground' : 'text-muted-foreground/60')">
              {{ ua.emailSenderAddress || '—' }}
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">
              Missed Call Notif. (Customer)
            </dt>
            <dd>
              @if (editMode()) {
                <hlm-switch [checked]="form.controls.missedCallNotificationEnabledByCustomer.value"
                  (checkedChange)="form.controls.missedCallNotificationEnabledByCustomer.setValue($any($event))" />
              } @else {
                <span class="text-sm font-medium text-foreground">
                  {{ ua.missedCallNotificationEnabledByCustomer | boolDisplay }}
                </span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">
              Missed Call Notif. (Marketing)
            </dt>
            <dd>
              @if (editMode()) {
                <hlm-switch [checked]="form.controls.missedCallNotificationEnabledByMarketing.value"
                  (checkedChange)="form.controls.missedCallNotificationEnabledByMarketing.setValue($any($event))" />
              } @else {
                <span class="text-sm font-medium text-foreground">
                  {{ ua.missedCallNotificationEnabledByMarketing | boolDisplay }}
                </span>
              }
            </dd>
          </div>

          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">
              2-Factor Auth
            </dt>
            <dd>
              @if (editMode()) {
                <hlm-switch [checked]="form.controls.enabled2FactorAuthentication.value"
                  (checkedChange)="form.controls.enabled2FactorAuthentication.setValue($any($event))" />
              } @else {
                <span class="text-sm font-medium text-foreground">
                  {{ ua.enabled2FactorAuthentication | boolDisplay }}
                </span>
              }
            </dd>
          </div>

          <!-- Telephone Password — read-only, masked by default, reveal audited (EB-3660) -->
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="cmt-eyebrow">
              Telephone Password
            </dt>
            <dd class="flex min-w-0 items-center justify-end gap-2">
              @if (ua.phonePassword) {
                <span
                  class="font-mono-feat text-sm tracking-[0.25em] text-foreground"
                  [attr.aria-label]="telPwRevealed() ? 'Telephone password shown' : 'Telephone password masked'"
                >
                  @if (telPwRevealed()) {
                    {{ ua.phonePassword }}
                  } @else {
                    ••••••••
                  }
                </span>
                <button
                  type="button"
                  class="grid h-7 w-7 flex-none place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
                  [attr.aria-label]="telPwRevealed() ? 'Hide telephone password' : 'Reveal telephone password'"
                  [attr.aria-pressed]="telPwRevealed()"
                  (click)="togglePhonePasswordReveal()"
                >
                  <hlm-icon [name]="telPwRevealed() ? 'lucideEyeOff' : 'lucideEye'" size="0.95rem" />
                </button>
              } @else {
                <span class="text-sm text-muted-foreground/60">—</span>
              }
            </dd>
          </div>
        </dl>
      </section>
    </div>

    <!-- Edit actions row (between data cards and ancillary sections); save/cancel in edit mode. -->
    <div class="flex flex-wrap items-start gap-2">
      @if (!editMode()) {
        <button hlmBtn variant="default" (click)="startEdit()">
          <hlm-icon name="lucidePencil" size="0.9rem" />
          Edit
        </button>
      } @else {
        <button hlmBtn variant="default" (click)="save()" [disabled]="form.invalid || saving()">
          @if (saving()) {
            <hlm-spinner size="16" />
            Saving...
          } @else {
            <hlm-icon name="lucideSave" size="0.9rem" />
            Save
          }
        </button>
        <button hlmBtn variant="outline" (click)="cancelEdit()">Cancel</button>
      }
    </div>

    <!-- EB-4453: Password Reset — a collapsible card, closed by default: it is an occasional
         action and keeps the page short. One row per channel: the contact it acts on (shown
         for information, even though Personal Data has it too), its status, and the actions.
         The prerequisite (a confirmed contact) and the actions share the SAME row, so the link
         between "confirm" and "reset" is visible: confirm first, then reset. The email channel
         (EB-4478) becomes a second row.
         Hidden while editing: the rows act on the SAVED data, the form may hold unsaved changes.
         Disabled-action hint = inline explainer, not a tooltip (Style Guide §17.2, EB-3811). -->
    @if (!editMode()) {
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm"
               data-testid="ua-password-reset-section">
        <h2>
          <button type="button"
                  class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 rounded-lg text-left transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  [attr.aria-expanded]="resetExpanded()"
                  (click)="toggleReset()"
                  data-testid="ua-password-reset-toggle">
            <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <hlm-icon name="lucideKeyRound" size="1rem" />
            </span>
            <span class="flex-1 font-display text-lg font-semibold text-foreground">Password Reset</span>
            <hlm-icon [name]="resetExpanded() ? 'lucideChevronUp' : 'lucideChevronDown'"
                      size="1.1rem" class="text-muted-foreground" />
          </button>
        </h2>

        @if (resetExpanded()) {
          <!-- A <div> inside a <dl> holds only dt/dd: the hint is a second <dd> of the same row. -->
          <dl class="mt-4 flex flex-col divide-y divide-border/70">
            <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2.5">
              <dt class="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                <span class="cmt-eyebrow">SMS</span>
                @if (ua.mobile) {
                  <span class="break-all font-mono-feat text-base text-foreground"
                        data-testid="ua-reset-sms-number">{{ ua.mobile }}</span>
                  @if (ua.mobileConfirmed) {
                    <hlm-badge variant="success">confirmed</hlm-badge>
                  } @else {
                    <hlm-badge variant="destructive">not confirmed</hlm-badge>
                  }
                } @else {
                  <hlm-badge variant="outline">no number</hlm-badge>
                }
              </dt>
              <dd class="flex flex-wrap items-center justify-end gap-2">
                <!-- EB-4453: old CMT "Send confirmation code" — only for a set-but-unconfirmed number. -->
                @if (canConfirmMobile()) {
                  <button hlmBtn variant="outline" (click)="confirmMobile()"
                          data-testid="ua-confirm-mobile-btn">
                    <hlm-icon name="lucideMessageSquare" size="0.9rem" />
                    Send confirmation code
                  </button>
                }
                <button hlmBtn variant="outline" (click)="resetPassword()"
                        [disabled]="!canResetPassword()"
                        [attr.aria-describedby]="canResetPassword() ? null : 'ua-reset-password-hint'"
                        data-testid="ua-reset-password-btn">
                  <hlm-icon name="lucideSend" size="0.9rem" />
                  Reset Password &amp; Send
                </button>
              </dd>
              @if (!canResetPassword()) {
                <dd id="ua-reset-password-hint" class="basis-full text-sm leading-snug text-foreground"
                    data-testid="ua-reset-password-disabled-hint">
                  @if (canConfirmMobile()) {
                    The reset is sent by SMS and needs a confirmed number — send a confirmation code first.
                  } @else {
                    Disabled — {{ resetPasswordDisabledReason() }}.
                  }
                </dd>
              }
            </div>
          </dl>
        }
      </section>
    }

    <!-- Phone Authentication (EB-3660) -->
    <cmt-phone-authentication-section (generateTan)="openGenerateTan()" />

    <!-- Related Phone Services -->
    @if (relatedPhoneServices().length > 0) {
      <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <header class="mb-4 flex items-center gap-2.5">
          <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <hlm-icon name="lucidePhone" size="1rem" />
          </span>
          <h2 class="font-display text-lg font-semibold text-foreground">Related Phone Services</h2>
          <hlm-badge variant="secondary" class="ml-1">{{ relatedPhoneServices().length }}</hlm-badge>
        </header>

        <!-- Status column intentionally omitted: the PS list endpoint doesn't
             return a status field. Status is visible on the PS detail page
             after click. The trailing chevron makes the row's clickability
             obvious (group-hover slides it slightly toward the navigation). -->
        <div class="overflow-hidden rounded-lg border border-border">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-muted/50">
                <th class="px-3 py-2 text-left cmt-eyebrow">ID</th>
                <th class="px-3 py-2 text-left cmt-eyebrow">Description</th>
                <th class="w-10 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-border/70">
              @for (ps of relatedPhoneServices(); track ps.id) {
                <tr class="group cursor-pointer transition-colors hover:bg-muted/30" (click)="navigateToPhoneService(ps.id)">
                  <td class="px-3 py-2 font-mono-feat text-foreground">{{ ps.id }}</td>
                  <td class="px-3 py-2 font-medium text-foreground">{{ ps.description }}</td>
                  <td class="px-3 py-2 text-right text-foreground/70 transition-all duration-150 group-hover:translate-x-1 group-hover:text-primary">
                    <hlm-icon name="lucideChevronRight" size="1.25rem" />
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    }

  </div>
}
